import React from 'react';
import { SidebarNavButton } from '../SidebarNavButton';
import { useFeatureAccess } from '@utils/featureFlag';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';

import { menuItems } from '../navigation';
import { useHistory } from 'react-router-dom';
import { UserButton } from './components/UserButton';
import { SidebarHeader } from './components/SidebarHeader';
import { TutorialModal } from '@components/organisms/TutorialModal';
import { useState, useMemo } from 'react';
import { usePendingIncidents } from '@hooks/api/incident/usePendingIncidents';
import { useOrganizationEventsSubscription } from '@hooks/api/useOrganizationEventsSubscription';
import { usePendingBudgetRequests } from '@hooks/api/budget/usePendingBudgetRequests';
import { useLowStockItems, useLeadTimeAlertCounts } from '@hooks/api/warehouse';
import { usePendingInvoicesCount } from '@hooks/api';
import { useProjects } from '@hooks/api/projects/useProjects';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { UserRoles } from '@models/user.model';
import { useFieldCaptureEnabled } from '@hooks/api/field-capture';
import { useTranslation } from '@hooks/useTranslation';
import { useAssistantAvailable } from '@hooks/api/assistant';
import { useDailySiteReportAvailable } from '@hooks/api/dailySiteReport';
import './styles.css';

export const LeftSidebar = () => {
  const history = useHistory();
  const [isTutorialModalOpen, setIsTutorialModalOpen] = useState(false);
  // Sprint 28, story-142. `menu.*` — docs/ux/story-142-design.md Wireframe A.
  const tMenu = useTranslation('menu');

  const { canAccess } = useFeatureAccess();

  // ATAV field-capture is org-gated at runtime (atavEnabled toggle) for
  // non-super-admins, on top of the role-based feature permission.
  const role = useAppSelector((state: RootState) => state.user.role);
  const { data: fieldCaptureEnabled } = useFieldCaptureEnabled(
    role !== UserRoles.SUPER_ADMIN && canAccess('FIELD_CAPTURE_ADMIN'),
  );

  // Sprint 32, story-165. `assistantAvailable` needs a live query (flag + Anthropic key
  // configured) on top of the static role-based FEATURE_PERMISSIONS gate below.
  const { data: assistantAvailable } = useAssistantAvailable();

  // Sprint 33, story-170. `dailySiteReportAvailable` needs a live query (feature flag) on top of
  // the static role-based FEATURE_PERMISSIONS gate below, same shape as Assistant above.
  const { data: dailySiteReportAvailable } = useDailySiteReportAvailable();

  // Filter menu items based on user permissions
  const filteredMenuItems = menuItems.filter((item) => {
    if (!canAccess(item.feature)) return false;
    if (item.link === '/admin/field-capture' && role !== UserRoles.SUPER_ADMIN) {
      return fieldCaptureEnabled === true;
    }
    if (item.name === 'Assistant') {
      return assistantAvailable === true;
    }
    if (item.name === 'Daily reports') {
      return dailySiteReportAvailable === true;
    }
    return true;
  });

  // Invoicing is gated by the INVOICES feature flag/permission. When it's off,
  // invoices must not be fetched or counted anywhere (Dashboard aggregate badge
  // included), mirroring the dashboard triage pill gating.
  const canInvoice = canAccess('INVOICES');

  const goToSettings = () => {
    history.push('/profile');
  };

  // Notification badges
  const { organizationId } = useAppSelector(
    (state: RootState) => state.organization,
  );

  const { data: pendingIncidents = [] } = usePendingIncidents();
  const pendingIncidentsCount = pendingIncidents.length;

  // Keeps every counter below live over one WebSocket, replacing the three 3s
  // polls these hooks used to run. Mounted here because the sidebar is always
  // rendered for an authenticated user, and mounted exactly once — the socket is
  // a shared singleton but each subscribe() opens its own stream.
  useOrganizationEventsSubscription();

  // The backend already scopes this to ACTIVE requests this organization takes
  // part in, which is the same set the Bids page counts. Re-filtering here is
  // what made the badge disagree with the page.
  const { data: budgetRequests = [] } = usePendingBudgetRequests(organizationId);
  const pendingBudgetsCount = budgetRequests.length;

  // Mirror the Warehouse secondary-sidebar "Alerts" badge: low stock + lead-time alerts.
  const { data: lowStockItems = [] } = useLowStockItems();
  const { data: leadTimeAlertCounts } = useLeadTimeAlertCounts();
  const warehouseAlertsCount = lowStockItems.length + (leadTimeAlertCounts?.total || 0);

  // Invoices badge: count of ACTIVE INVOICE_PENDING activities across all org projects,
  // mirroring the dashboard invoice triage pill (usePendingInvoicesCount).
  const { data: projects = [] } = useProjects();
  const projectsIds = useMemo(
    () =>
      projects
        .map((project) => project.projectId)
        .filter((id): id is string => Boolean(id)),
    [projects],
  );
  // Skip polling invoices entirely when the feature is off.
  const { data: pendingInvoicesCount = 0 } = usePendingInvoicesCount(
    canInvoice ? projectsIds : [],
  );

  // Dashboard badge: sum of every alert source surfaced individually below
  // (incidents + actionable budgets + warehouse alerts + pending invoices).
  // Invoices only count toward the total when the feature is enabled.
  const dashboardAlertsCount =
    pendingIncidentsCount +
    pendingBudgetsCount +
    warehouseAlertsCount +
    (canInvoice ? pendingInvoicesCount : 0);

  const getIconRight = (item: (typeof menuItems)[0]): React.ReactNode => {
    if (item.name === 'Dashboard' && dashboardAlertsCount > 0) {
      return (
        <span className="bg-red-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none">
          {dashboardAlertsCount}
        </span>
      );
    }
    if (item.name === 'Incidents' && pendingIncidentsCount > 0) {
      return (
        <span className="bg-red-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none">
          {pendingIncidentsCount}
        </span>
      );
    }
    if (item.name === 'Bids' && pendingBudgetsCount > 0) {
      return (
        <span className="bg-amber-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none">
          {pendingBudgetsCount}
        </span>
      );
    }
    if (item.name === 'Warehouse' && warehouseAlertsCount > 0) {
      return (
        <span className="bg-yellow-500 text-yellow-900 text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none">
          {warehouseAlertsCount}
        </span>
      );
    }
    if (item.name === 'Invoices' && pendingInvoicesCount > 0) {
      return (
        <span className="bg-amber-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none">
          {pendingInvoicesCount}
        </span>
      );
    }
    if (item.rightSidebar) {
      return (
        <ArrowForwardOutlinedIcon
          sx={{ fontSize: 14 }}
          className="text-white"
        />
      );
    }
    return undefined;
  };

  return (
    <aside className="w-52 bg-indigo-900 dark:bg-gray-900 text-white flex flex-col transition-colors duration-200 h-screen">
      <div className="flex flex-col flex-1 overflow-y-auto custom-scrollbar">
        <SidebarHeader />
        {/* Sprint 16, story-075 (i03ePmni). `ApprovalsNotification`'s clipped dropdown panel was
            removed from here — approvals now live in the dashboard's "Needs your action" block
            (`NeedsYourActionBlock`), which reuses `ApprovalItem` directly. */}
        <nav className="mt-2 flex-1">
          {filteredMenuItems.map((item) => (
            <SidebarNavButton
              key={item.name}
              link={item.link}
              label={tMenu[item.translationKey as keyof typeof tMenu] || item.name}
              iconLeft={item.icon}
              preserveId={item.rightSidebar}
              onClick={
                item.isModal && item.name === 'Tutorials'
                  ? () => setIsTutorialModalOpen(true)
                  : undefined
              }
              iconRight={getIconRight(item)}
            />
          ))}
        </nav>
      </div>

      <div className="flex flex-col px-4 py-2 items-start border-t border-indigo-800 dark:border-gray-800">
        <UserButton goToSettings={goToSettings} />
      </div>

      <TutorialModal
        isOpen={isTutorialModalOpen}
        onClose={() => setIsTutorialModalOpen(false)}
      />
    </aside>
  );
};
